/**
 * Per-row owner resolver for Lighthouse Agents list.
 *
 * The list endpoint /api/v1/lighthouse/agents does NOT include owner info.
 * Owner is derived from the agent's sub-identities, specifically the IdP
 * binding (type === "idp"), which is only available via
 * /api/v1/lighthouse/agents/{id}/sub-identities.
 *
 * To avoid hammering the backend, callers should only enable this hook
 * when the row is actually visible (see `useInView` below).
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { lighthouseGet } from "@/services/lighthouseApi";
import { useConnectorEnabled } from "@/hooks/connectors/useConnector";

interface SubIdentityRow {
  type?: string;
  plane?: string;
  payload?: { name?: string; email?: string; [k: string]: unknown };
  display_name?: string;
  identifier?: string;
  [k: string]: unknown;
}

function pickOwner(rows: SubIdentityRow[]): string | undefined {
  const idp = rows.find(
    (r) => (r.type ?? "").toLowerCase() === "idp" || (r.plane ?? "").toLowerCase() === "idp",
  );
  if (!idp) return undefined;
  return (
    idp.payload?.email ||
    idp.payload?.name ||
    idp.display_name ||
    idp.identifier ||
    undefined
  );
}

export function useLighthouseAgentOwner(agentId: string | undefined, enabled: boolean) {
  const connectorOn = useConnectorEnabled("lighthouse");
  return useQuery({
    enabled: connectorOn && !!agentId && enabled,
    queryKey: ["lighthouse", "agent", agentId, "owner"],
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
    queryFn: async (): Promise<string | undefined> => {
      const { data } = await lighthouseGet<SubIdentityRow[] | { sub_identities?: SubIdentityRow[] }>(
        `/api/v1/lighthouse/agents/${agentId}/sub-identities`,
      );
      const rows = Array.isArray(data) ? data : (data?.sub_identities ?? []);
      return pickOwner(rows);
    },
  });
}

/**
 * IntersectionObserver-based visibility hook. Returns `true` once the
 * element has scrolled into the viewport (sticky — does not flip back to
 * false on scroll-out so the cached owner string remains rendered).
 */
export function useInView<T extends Element>(rootMargin = "200px") {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView, rootMargin]);

  return { ref, inView };
}
